'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, use } from 'react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Dialog, DialogClose, DialogPortal, DialogPrimitiveContent, DialogTitle } from '@/components/ui/dialog';
import ViverseButton from '@/components/ViverseButton';
import ViverseWorldCard from '@/components/ViverseWorldCard';
import { NEW_TAB_LINK_PROPS } from '@/constants/card';
import { useIsEmbeddedControlBar } from '@/context/EmbeddedControlBarContext';
import { RECOMMEND_VIEW_ALL_THRESHOLD, RECOMMEND_WORLDS_LIMIT } from '@/constants/recommend';
import { Link } from '@/i18n/navigation';
import { LogoOverlayContentKind, type LogoOverlayContentModel } from '@/lib/logo-overlay';
import type { RecommendRoom } from '@/services/cms';
type ControlBarRecommendationOverlayProps = {
  model: LogoOverlayContentModel;
  onCloseAction: () => void;
  open: boolean;
  portalContainer: HTMLElement;
  roomsPromise: Promise<RecommendRoom[]> | null;
};
type RecommendationGridProps = {
  cardType: 'experiences' | 'games';
  roomsPromise: Promise<RecommendRoom[]>;
};
type RecommendationContentProps = RecommendationGridProps & {
  ctaLabel: string;
  model: LogoOverlayContentModel;
  onCloseAction: () => void;
  openLinksInNewTab: boolean;
};
function RecommendationContent(t0) {
  const $ = _c(18);
  const {
    cardType,
    ctaLabel,
    model,
    onCloseAction,
    openLinksInNewTab,
    roomsPromise
  } = t0;
  const rooms = use(roomsPromise);
  let t1;
  if ($[0] !== cardType || $[1] !== openLinksInNewTab || $[2] !== rooms) {
    let t2;
    if ($[4] !== cardType || $[5] !== openLinksInNewTab) {
      t2 = room => <ViverseWorldCard key={room.id} cardType={cardType} className="rounded-none p-0 shadow-none lg:hover:shadow-none" hideLiked={true} hidePremiumBadge={true} imageSizes="(max-width: 767px) 50vw, 210px" info={room} mediaClassName="rounded-2xl" openLinksInNewTab={openLinksInNewTab} />;
      $[4] = cardType;
      $[5] = openLinksInNewTab;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = rooms.map(t2);
    $[0] = cardType;
    $[1] = openLinksInNewTab;
    $[2] = rooms;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className="grid w-full max-w-[912px] grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== ctaLabel || $[10] !== model || $[11] !== onCloseAction || $[12] !== openLinksInNewTab || $[13] !== rooms.length) {
    t3 = rooms.length < RECOMMEND_VIEW_ALL_THRESHOLD && model.viewAllHref && <div className="mt-6 pb-8"><Link href={model.viewAllHref} onClick={onCloseAction} {...openLinksInNewTab && NEW_TAB_LINK_PROPS}><ViverseButton variant="default" size="medium">{ctaLabel}</ViverseButton></Link></div>;
    $[9] = ctaLabel;
    $[10] = model;
    $[11] = onCloseAction;
    $[12] = openLinksInNewTab;
    $[13] = rooms.length;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== t2 || $[16] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}
function RecommendationGridSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="grid w-full max-w-[912px] grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">{Array.from({
        length: RECOMMEND_WORLDS_LIMIT
      }, _temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(_, index) {
  return <div key={index} className="flex flex-col gap-2"><div className="bg-vive-primary-70 aspect-430/242 animate-pulse rounded-2xl" /><div className="bg-vive-primary-70 h-4 w-3/4 animate-pulse rounded-sm" /><div className="bg-vive-primary-70 h-3 w-1/2 animate-pulse rounded-sm" /></div>;
}
export default function ControlBarRecommendationOverlay(t0) {
  const $ = _c(31);
  const {
    model,
    onCloseAction,
    open,
    portalContainer,
    roomsPromise
  } = t0;
  const t = useTranslations("exit-recommendation-modal");
  const cardType = model.contentKind === LogoOverlayContentKind.GAME ? "games" : "experiences";
  const openLinksInNewTab = useIsEmbeddedControlBar();
  let t1;
  if ($[0] !== onCloseAction) {
    t1 = nextOpen => {
      if (!nextOpen) {
        onCloseAction();
      }
    };
    $[0] = onCloseAction;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("close");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <X className="size-6" aria-hidden="true" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <DialogClose aria-label={t2} className="bg-vive-primary-70 absolute top-7 right-6 z-10 inline-flex size-[34px] items-center justify-center rounded-full text-white">{t3}</DialogClose>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("title");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <DialogTitle className="mb-6 text-center text-2xl leading-8 font-extrabold tracking-[0.1px] text-white md:text-[30px] md:leading-10">{t5}</DialogTitle>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== cardType || $[12] !== model || $[13] !== onCloseAction || $[14] !== openLinksInNewTab || $[15] !== roomsPromise || $[16] !== t) {
    t7 = roomsPromise ? <Suspense fallback={<RecommendationGridSkeleton />}><RecommendationContent cardType={cardType} ctaLabel={t(`cta.${cardType}`)} model={model} onCloseAction={onCloseAction} openLinksInNewTab={openLinksInNewTab} roomsPromise={roomsPromise} /></Suspense> : <RecommendationGridSkeleton />;
    $[11] = cardType;
    $[12] = model;
    $[13] = onCloseAction;
    $[14] = openLinksInNewTab;
    $[15] = roomsPromise;
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <div className="flex min-h-full flex-col items-center px-4 pt-20 pb-12 md:px-16 md:pt-14">{t6}{t7}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t4 || $[22] !== t8) {
    t9 = <DialogPrimitiveContent data-component="control-bar-recommendation-overlay" className="z-overlay absolute inset-0 overflow-y-auto bg-[rgba(19,27,38,0.7)] backdrop-blur-[50px] outline-none" onInteractOutside={_temp2}>{t4}{t8}</DialogPrimitiveContent>;
    $[21] = t4;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== portalContainer || $[25] !== t9) {
    t10 = <DialogPortal container={portalContainer}>{t9}</DialogPortal>;
    $[24] = portalContainer;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== open || $[28] !== t1 || $[29] !== t10) {
    t11 = <Dialog modal={false} open={open} onOpenChange={t1}>{t10}</Dialog>;
    $[27] = open;
    $[28] = t1;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
}
function _temp2(event) {
  return event.preventDefault();
}